import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Box from "../../../setupPage/SharedComponents/Box";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import SetupContainer from "../../../setupPage/SharedComponents/SetupContainer";
import SubmitBtn from "../../../setupPage/SharedComponents/SubmitBtn";
import {
  getPermissionMatrix,
  getPermissionUsersByRole,
  getUserPermissions,
  resetUserPermissions,
  saveRolePermissions,
  saveUserPermissions,
} from "../../../../api/permissionApi";
import { usePermission } from "../../../../context/PermissionContext";

const emptyGrant = () => ({
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
});

const grantsEqual = (a = emptyGrant(), b = emptyGrant()) =>
  !!a.canView === !!b.canView &&
  !!a.canCreate === !!b.canCreate &&
  !!a.canEdit === !!b.canEdit &&
  !!a.canDelete === !!b.canDelete;

const RolesPermissions = () => {
  const { isAdmin, canView, reload } = usePermission();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [screens, setScreens] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [grantsByRole, setGrantsByRole] = useState({});
  const [userGrants, setUserGrants] = useState(null);
  const [userIsCustom, setUserIsCustom] = useState(false);
  const [screenQuery, setScreenQuery] = useState("");

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const matrix = await getPermissionMatrix();
      const nextRoles = matrix.roles || matrix.Roles || [];
      const nextScreens = matrix.screens || matrix.Screens || [];
      const nextGrants = matrix.grants || matrix.Grants || [];
      const grouped = {};
      nextGrants.forEach((g) => {
        const roleId = g.identityRoleId || g.IdentityRoleId;
        const code = g.screenCode || g.ScreenCode;
        if (!roleId || !code) return;
        grouped[roleId] = grouped[roleId] || {};
        grouped[roleId][code] = {
          canView: !!(g.canView ?? g.CanView),
          canCreate: !!(g.canCreate ?? g.CanCreate),
          canEdit: !!(g.canEdit ?? g.CanEdit),
          canDelete: !!(g.canDelete ?? g.CanDelete),
        };
      });
      setRoles(nextRoles);
      setScreens(nextScreens);
      setGrantsByRole(grouped);
      setSelectedRoleId((prev) => prev || nextRoles[0]?.id || nextRoles[0]?.Id || "");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not load permissions",
        text: err.response?.data || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatrix();
  }, []);

  useEffect(() => {
    if (!selectedRoleId) {
      setUsers([]);
      setSelectedUserId("");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const list = await getPermissionUsersByRole(selectedRoleId);
        if (cancelled) return;
        setUsers(list);
        setSelectedUserId("");
        setUserGrants(null);
        setUserIsCustom(false);
      } catch {
        if (!cancelled) setUsers([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRoleId]);

  useEffect(() => {
    if (!selectedUserId) {
      setUserGrants(null);
      setUserIsCustom(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await getUserPermissions(selectedUserId);
        if (cancelled) return;
        const next = {};
        (result.grants || result.Grants || []).forEach((g) => {
          const code = g.screenCode || g.ScreenCode;
          if (!code) return;
          next[code] = {
            canView: !!(g.canView ?? g.CanView),
            canCreate: !!(g.canCreate ?? g.CanCreate),
            canEdit: !!(g.canEdit ?? g.CanEdit),
            canDelete: !!(g.canDelete ?? g.CanDelete),
          };
        });
        const custom = !!(result.isCustom ?? result.IsCustom);
        setUserIsCustom(custom);
        setUserGrants(custom ? next : null);
      } catch {
        if (!cancelled) {
          setUserIsCustom(false);
          setUserGrants(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedUserId]);

  const roleGrants = grantsByRole[selectedRoleId] || {};
  const selectedGrants = selectedUserId ? userGrants || roleGrants : roleGrants;
  const editingUser = Boolean(selectedUserId);
  const showingInherited = editingUser && !userIsCustom;

  const groupedScreens = useMemo(() => {
    const groups = { top: [], crm: [], setup: [], settings: [] };
    screens.forEach((s) => {
      const parent = s.parentCode || s.ParentCode;
      const item = {
        code: s.code || s.Code,
        name: s.name || s.Name,
        supportsCreate: !!(s.supportsCreate ?? s.SupportsCreate),
        supportsEdit: !!(s.supportsEdit ?? s.SupportsEdit),
        supportsDelete: !!(s.supportsDelete ?? s.SupportsDelete),
        parent,
      };
      if (parent === "setup") groups.setup.push(item);
      else if (parent === "settings") groups.settings.push(item);
      else if (parent === "crm") groups.crm.push(item);
      else groups.top.push(item);
    });
    return groups;
  }, [screens]);

  const filteredGroups = useMemo(() => {
    const q = screenQuery.trim().toLowerCase();
    const match = (item) =>
      !q ||
      item.name.toLowerCase().includes(q) ||
      String(item.code || "").toLowerCase().includes(q);
    return {
      top: groupedScreens.top.filter(match),
      crm: groupedScreens.crm.filter(match),
      settings: groupedScreens.settings.filter(match),
      setup: groupedScreens.setup.filter(match),
    };
  }, [groupedScreens, screenQuery]);

  const visibleScreenCount =
    filteredGroups.top.length +
    filteredGroups.crm.length +
    filteredGroups.settings.length +
    filteredGroups.setup.length;
  const totalScreenCount = screens.length;
  const selectedUser = users.find((u) => (u.id || u.Id) === selectedUserId);
  const selectedUserName =
    selectedUser?.userName || selectedUser?.UserName || "This user";

  const setFlag = (code, flag, value) => {
    const apply = (currentMap) => {
      const next = { ...(currentMap || {}) };
      const current = { ...emptyGrant(), ...(next[code] || {}) };
      current[flag] = value;
      if (flag === "canView" && !value) {
        current.canCreate = false;
        current.canEdit = false;
        current.canDelete = false;
      }
      if (flag !== "canView" && value) {
        current.canView = true;
      }
      next[code] = current;
      return next;
    };

    if (editingUser) {
      setUserIsCustom(true);
      setUserGrants((prev) => apply(prev || roleGrants));
      return;
    }

    setGrantsByRole((prev) => ({
      ...prev,
      [selectedRoleId]: apply(prev[selectedRoleId]),
    }));
  };

  const buildGrantPayload = (map) =>
    Object.entries(map || {}).map(([screenCode, flags]) => ({
      identityRoleId: selectedRoleId,
      screenCode,
      canView: !!flags.canView,
      canCreate: !!flags.canCreate,
      canEdit: !!flags.canEdit,
      canDelete: !!flags.canDelete,
    }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      if (editingUser) {
        await saveUserPermissions(selectedUserId, buildGrantPayload(selectedGrants));
        setUserIsCustom(true);
        setUsers((prev) =>
          prev.map((u) =>
            (u.id || u.Id) === selectedUserId
              ? { ...u, hasCustomPermissions: true, HasCustomPermissions: true }
              : u,
          ),
        );
        await reload();
        Swal.fire({
          icon: "success",
          title: "Saved for this user only",
          text: "Other people in this role keep the role default unless they also have custom permissions.",
          timer: 2200,
          showConfirmButton: false,
        });
      } else {
        const matrix = await saveRolePermissions(selectedRoleId, buildGrantPayload(selectedGrants));
        const nextGrants = matrix.grants || matrix.Grants || [];
        const grouped = {};
        nextGrants.forEach((g) => {
          const roleId = g.identityRoleId || g.IdentityRoleId;
          const code = g.screenCode || g.ScreenCode;
          if (!roleId || !code) return;
          grouped[roleId] = grouped[roleId] || {};
          grouped[roleId][code] = {
            canView: !!(g.canView ?? g.CanView),
            canCreate: !!(g.canCreate ?? g.CanCreate),
            canEdit: !!(g.canEdit ?? g.CanEdit),
            canDelete: !!(g.canDelete ?? g.CanDelete),
          };
        });
        setGrantsByRole(grouped);
        await reload();
        Swal.fire({
          icon: "success",
          title: "Saved for the whole role",
          text: "Applies to every user in this role who does not have custom permissions.",
          timer: 2200,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: err.response?.data || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetUser = async () => {
    if (!selectedUserId) return;
    const confirm = await Swal.fire({
      icon: "question",
      title: "Use role default for this user?",
      text: "Their custom checkboxes will be removed. They will follow the role again.",
      showCancelButton: true,
      confirmButtonText: "Reset to role",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      await resetUserPermissions(selectedUserId);
      setUserIsCustom(false);
      setUserGrants(null);
      setUsers((prev) =>
        prev.map((u) =>
          (u.id || u.Id) === selectedUserId
            ? { ...u, hasCustomPermissions: false, HasCustomPermissions: false }
            : u,
        ),
      );
      await reload();
      Swal.fire({
        icon: "success",
        title: "User now follows the role default",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Reset failed",
        text: err.response?.data || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderRows = (items) =>
    items.map((screen) => {
      const grant = selectedGrants[screen.code] || emptyGrant();
      const roleGrant = roleGrants[screen.code] || emptyGrant();
      const differsFromRole = editingUser && !grantsEqual(grant, roleGrant);
      return (
        <tr
          key={screen.code}
          className={`border-b border-stone-100 last:border-0 hover:bg-stone-50/80 ${
            differsFromRole ? "bg-amber-50 hover:bg-amber-50" : "bg-white"
          }`}
        >
          <td className="py-2.5 px-4 text-sm font-medium text-stone-800">
            <span>{screen.name}</span>
            {differsFromRole ? (
              <span className="ml-2 inline-flex align-middle rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                custom
              </span>
            ) : null}
          </td>
          {["canView", "canCreate", "canEdit", "canDelete"].map((flag) => {
            const supported =
              flag === "canView" ||
              (flag === "canCreate" && screen.supportsCreate) ||
              (flag === "canEdit" && screen.supportsEdit) ||
              (flag === "canDelete" && screen.supportsDelete);
            return (
              <td key={flag} className="py-2 px-3 text-center w-[92px]">
                {supported ? (
                  <label className="inline-flex h-8 w-8 items-center justify-center rounded-lg cursor-pointer hover:bg-stone-100">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-orange-600 cursor-pointer"
                      checked={!!grant[flag]}
                      onChange={(e) => setFlag(screen.code, flag, e.target.checked)}
                      aria-label={`${screen.name} ${flag.replace("can", "")}`}
                    />
                  </label>
                ) : (
                  <span className="text-stone-300">—</span>
                )}
              </td>
            );
          })}
        </tr>
      );
    });

  const renderGroup = (title, items) => {
    if (!items.length) return null;
    return (
      <>
        <tr>
          <td colSpan={5} className="bg-stone-50 py-2 px-4 border-y border-stone-100">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
                {title}
              </span>
              <span className="inline-flex min-w-[18px] justify-center rounded-full bg-white px-1.5 text-[10px] font-semibold text-stone-500 border border-stone-200">
                {items.length}
              </span>
            </div>
          </td>
        </tr>
        {renderRows(items)}
      </>
    );
  };

  if (!isAdmin && !canView("roles-permissions")) {
    return (
      <Box top>
        <p className="text-lg text-red-600">Only Admin can manage role permissions.</p>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box top>
        <p className="text-lg">Loading permissions…</p>
      </Box>
    );
  }

  return (
    <div>
      <Box top>
        <SetupTittle text="Roles & permissions" />
        <SetupContainer>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid gap-3 lg:grid-cols-3">
              <label className="block text-sm font-semibold text-stone-700">
                Identity role
                <select
                  className="mt-1 block w-full h-11 border border-stone-200 rounded-xl px-3 bg-stone-50 focus:bg-white"
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                >
                  {roles.map((role) => {
                    const id = role.id || role.Id;
                    const name = role.name || role.Name;
                    return (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="block text-sm font-semibold text-stone-700">
                User (optional)
                <select
                  className="mt-1 block w-full h-11 border border-stone-200 rounded-xl px-3 bg-stone-50 focus:bg-white"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">All users in this role (default)</option>
                  {users.map((user) => {
                    const id = user.id || user.Id;
                    const name = user.userName || user.UserName || id;
                    const email = user.email || user.Email;
                    const custom = !!(user.hasCustomPermissions ?? user.HasCustomPermissions);
                    return (
                      <option key={id} value={id}>
                        {name}
                        {email ? ` (${email})` : ""}
                        {custom ? " — custom" : ""}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="block text-sm font-semibold text-stone-700">
                Search screens
                <div className="relative mt-1">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-stone-400">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    value={screenQuery}
                    onChange={(e) => setScreenQuery(e.target.value)}
                    placeholder="Walk-in, invoice, city…"
                    className="block w-full h-11 pl-9 pr-16 border border-stone-200 rounded-xl bg-stone-50 focus:bg-white"
                    autoComplete="off"
                  />
                  {screenQuery ? (
                    <button
                      type="button"
                      onClick={() => setScreenQuery("")}
                      className="absolute inset-y-0 right-2 my-auto h-7 px-2 rounded-md text-xs font-semibold text-stone-500 hover:bg-stone-100"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              </label>
            </div>

            <div
              className={`rounded-xl border px-3.5 py-2.5 text-sm leading-relaxed ${
                editingUser
                  ? userIsCustom
                    ? "border-amber-200 bg-amber-50 text-amber-950"
                    : "border-sky-200 bg-sky-50 text-sky-950"
                  : "border-stone-200 bg-stone-50 text-stone-600"
              }`}
            >
              {!editingUser ? (
                <p>
                  Editing the <strong>role default</strong>. This applies to every user in
                  this role who does not have custom permissions. Pick a user to give that
                  person a different set.
                </p>
              ) : showingInherited ? (
                <p>
                  <strong>{selectedUserName}</strong> currently follows the role default.
                  Change a checkbox and save to make permissions apply only to them.
                </p>
              ) : (
                <p>
                  Editing <strong>custom permissions</strong> for this user only. Amber rows
                  differ from the role default.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-stone-500">
                {screenQuery.trim()
                  ? `${visibleScreenCount} of ${totalScreenCount} screens`
                  : `${totalScreenCount} screens`}
              </p>
            </div>

            <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
              <div className="max-h-[min(62vh,720px)] overflow-auto">
                <table className="min-w-full">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-stone-100 text-left text-sm">
                      <th className="py-2.5 px-4">Screen</th>
                      <th className="py-2.5 px-3 text-center w-[92px]">View</th>
                      <th className="py-2.5 px-3 text-center w-[92px]">Create</th>
                      <th className="py-2.5 px-3 text-center w-[92px]">Edit</th>
                      <th className="py-2.5 px-3 text-center w-[92px]">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleScreenCount ? (
                      <>
                        {renderGroup("Admin screens", filteredGroups.top)}
                        {renderGroup("CRM", filteredGroups.crm)}
                        {renderGroup("Settings", filteredGroups.settings)}
                        {renderGroup("Set-up", filteredGroups.setup)}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={5} className="py-12 px-4 text-center text-sm text-stone-500">
                          No screens match “{screenQuery.trim()}”.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <SubmitBtn text={saving ? "Saving" : "Save"} />
              {editingUser && userIsCustom ? (
                <button
                  type="button"
                  className="font-medium text-sm rounded-xl h-10 px-4 text-white bg-stone-600 hover:bg-stone-700 disabled:opacity-60"
                  onClick={handleResetUser}
                  disabled={saving}
                >
                  Reset to role default
                </button>
              ) : null}
            </div>
          </form>
        </SetupContainer>
      </Box>
    </div>
  );
};

export default RolesPermissions;
